/**
 * Typed analytics service layer. Wraps raw API calls and returns fully typed shapes.
 * Backend shape changes produce TypeScript errors at this boundary.
 * Used by useProjectAnalytics; effectiveSchedule (S-curve fallback) remains built in the hook.
 */

import { getScheduleAnalytics, getRiskAnalytics } from '@/api/analyticsApi';
import { getXERProjectTasks, getXERProjectRelationships } from '@/api/schedule';
import type { ScheduleAnalyticsResponse } from '@/types/analyticsApi';
import type { RiskAnalytics } from '@/types/analytics';
import type { RelationshipRow } from '@/types/analyticsApi';

export type { ScheduleAnalyticsResponse, RelationshipRow };

/** Raw project tasks response: may be { activities, udf_labels } or array. Service returns as-is for normalization in hook. */
export interface ProjectTasksResponse {
  activities: unknown;
  udf_labels: Record<string, string>;
}

/**
 * Fetch schedule analytics with typed response. Returns null on failure or missing project.
 * Optional snapshotId scopes to that import (e.g. baseline snapshot for S-curve comparison).
 */
export async function fetchScheduleAnalytics(
  projectId: string,
  snapshotId?: string | null
): Promise<ScheduleAnalyticsResponse | null> {
  try {
    const data = await getScheduleAnalytics(projectId, snapshotId);
    if (data != null && typeof data === 'object') {
      return data as ScheduleAnalyticsResponse;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch risk analytics with typed response. Returns null on failure.
 */
export async function fetchRiskAnalytics(projectId: string): Promise<RiskAnalytics | null> {
  try {
    const data = await getRiskAnalytics(projectId);
    if (data != null && typeof data === 'object') {
      return data as RiskAnalytics;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch project tasks (activities). Returns raw response for normalization in useProjectAnalytics.
 */
export async function fetchProjectTasks(projectId: string): Promise<ProjectTasksResponse> {
  const data = await getXERProjectTasks(projectId);
  if (Array.isArray(data)) {
    return { activities: data, udf_labels: {} };
  }
  if (data != null && typeof data === 'object' && 'activities' in data) {
    const d = data as { activities?: unknown; udf_labels?: Record<string, string> };
    return {
      activities: d.activities ?? [],
      udf_labels: d.udf_labels ?? {},
    };
  }
  return { activities: [], udf_labels: {} };
}

/**
 * Fetch project relationships with typed array. Returns empty array on failure.
 */
export async function fetchProjectRelationships(projectId: string): Promise<RelationshipRow[]> {
  try {
    const data = await getXERProjectRelationships(projectId);
    if (Array.isArray(data)) {
      return data as RelationshipRow[];
    }
    return [];
  } catch {
    return [];
  }
}
